// Attribution params we forward to the legacy Rails app. These match the
// keys read by public_app's ApplicationController#gtm_cookie and
// Api::V1::DemoRequestsController#utm_params.
//
// URL_FORWARDABLE keys are the ones we attach to outbound URLs (Rails reads
// them from the query string). The session-only keys (first_page, referral)
// are derived once on first landing and forwarded only in form payloads —
// they don't belong in URLs.
export const URL_FORWARDABLE = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_term',
  'utm_content',
  'gclid',
] as const

const ATTRIBUTION_KEYS = [...URL_FORWARDABLE, 'first_page', 'referral'] as const

type AttributionKey = (typeof ATTRIBUTION_KEYS)[number]
type AttributionParams = Partial<Record<AttributionKey, string>>

const STORAGE_KEY = 'paved_attribution'
const MAX_FIELD_LENGTH = 244 // mirror Rails' cookies.signed["entry"][0..244]

const isBrowser = () => typeof window !== 'undefined'

const readFromUrl = (search: string): AttributionParams => {
  const params = new URLSearchParams(search)
  const result: AttributionParams = {}
  for (const key of URL_FORWARDABLE) {
    const value = params.get(key)
    if (value) result[key] = value
  }
  return result
}

// Strip the referer down to the external host the user came from. We skip
// internal paved.com navigations (mirrors Rails' gtm_cookie behavior).
const readReferral = (): string | undefined => {
  const ref = document.referrer
  if (!ref) return undefined
  try {
    const host = new URL(ref).host
    if (host.includes('paved')) return undefined
    return ref.slice(0, MAX_FIELD_LENGTH)
  } catch {
    return undefined
  }
}

/**
 * Capture attribution params from the current page and persist for the session.
 * - URL-derived params (utm_*, gclid) read from the query string.
 * - Session-derived params (first_page, referral) captured from the landing
 *   page on first call only.
 * First-touch wins per key.
 */
export const captureFromUrl = (): void => {
  if (!isBrowser()) return

  const existing = getStoredParamsRaw()
  const fresh: AttributionParams = readFromUrl(window.location.search)

  if (!existing.first_page) {
    fresh.first_page = window.location.href.slice(0, MAX_FIELD_LENGTH)
  }
  if (!existing.referral) {
    const referral = readReferral()
    if (referral) fresh.referral = referral
  }

  const merged: AttributionParams = { ...fresh, ...existing }
  if (Object.keys(merged).length === 0) return

  try {
    window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(merged))
  } catch {
    // sessionStorage can throw in private mode / quota errors — fail silently
  }
}

// Read-only accessor that does NOT fall back to URL — used internally during
// capture to avoid the URL fallback re-reading freshly-captured-URL params.
const getStoredParamsRaw = (): AttributionParams => {
  if (!isBrowser()) return {}
  try {
    const raw = window.sessionStorage.getItem(STORAGE_KEY)
    if (!raw) return {}
    const parsed = JSON.parse(raw)
    return parsed && typeof parsed === 'object' ? parsed : {}
  } catch {
    return {}
  }
}

export const getStoredParams = (): AttributionParams => {
  const stored = getStoredParamsRaw()
  if (Object.keys(stored).length > 0) return stored
  // Fallback: read directly from the current URL. Covers the first render on
  // the landing page itself, where AttributionCapture's effect may not have
  // run yet by the time a Link's own effect computes its href.
  if (!isBrowser()) return {}
  return readFromUrl(window.location.search)
}

/**
 * Append stored attribution params to a URL's query string. Existing params
 * on the URL take precedence — we only fill in keys the URL doesn't already set.
 */
export const appendParams = (href: string): string => {
  // Stored params live in sessionStorage and the live URL — both browser-only.
  // Every caller invokes this from useEffect or onSubmit, so we never need a
  // server-side base URL for resolving relative paths.
  if (!isBrowser()) return href

  const stored = getStoredParams()
  if (!URL_FORWARDABLE.some((key) => stored[key])) return href

  const isAbsolute = /^https?:\/\//i.test(href)
  let url: URL
  try {
    url = isAbsolute ? new URL(href) : new URL(href, window.location.origin)
  } catch {
    return href
  }

  // Only attach URL-friendly keys to outbound links. first_page and referral
  // are session-context that belongs in form payloads, not query strings.
  for (const key of URL_FORWARDABLE) {
    if (stored[key] && !url.searchParams.has(key)) {
      url.searchParams.set(key, stored[key]!)
    }
  }

  return isAbsolute ? url.toString() : `${url.pathname}${url.search}${url.hash}`
}
